import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { toast } from "sonner";
import { RefreshCwIcon, PlusIcon, SearchIcon } from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import {
  BaseModelCandidateService,
  BaseModelCandidate,
} from "../../services/BaseModelCandidateService";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import BaseModelCandidatesTable from "./BaseModelCandidatesTable";

const BaseModelCandidatesOverview: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [candidatesByPage, setCandidatesByPage] = useState<
    Record<string, BaseModelCandidate[]>
  >({});
  const pageSize = 10;
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addHfModelId, setAddHfModelId] = useState("");
  const [addDisplayName, setAddDisplayName] = useState("");
  const [addSkipDiscovery, setAddSkipDiscovery] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [discoveringId, setDiscoveringId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 400);

  const cacheKey = `${debouncedSearch || ""}-${currentPage}`;

  const loadCandidates = useCallback(() => {
    if (!apiKey) return;

    if (!candidatesByPage[cacheKey]) {
      const offset = (currentPage - 1) * pageSize;
      BaseModelCandidateService.listCandidates(
        setIsLoading,
        apiKey,
        undefined,
        debouncedSearch || undefined,
        pageSize,
        offset
      ).subscribe(
        (response: { candidates: BaseModelCandidate[]; total: number }) => {
          setCandidatesByPage((prev) => ({
            ...prev,
            [cacheKey]: response.candidates,
          }));
          setTotalCandidates(response.total);
          setIsLoading(false);
        },
        (error: unknown) => {
          console.error("Failed to load candidates:", error);
          toast.error("Failed to load candidates");
          setIsLoading(false);
        }
      );
    }
  }, [apiKey, currentPage, debouncedSearch, cacheKey, candidatesByPage]);

  useEffect(() => {
    setCurrentPage(1);
    setCandidatesByPage({});
  }, [debouncedSearch]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const fetchCandidates = useCallback(() => {
    setCandidatesByPage({});
    setCurrentPage(1);
  }, []);

  const handleAdd = () => {
    const hfId = addHfModelId.trim();
    if (!hfId) {
      toast.error("HuggingFace model ID is required");
      return;
    }
    setAddSubmitting(true);
    BaseModelCandidateService.createCandidate(
      hfId,
      addDisplayName.trim() || null,
      addSkipDiscovery,
      null,
      apiKey
    ).subscribe(
      () => {
        toast.success("Candidate added");
        setAddDialogOpen(false);
        setAddHfModelId("");
        setAddDisplayName("");
        setAddSkipDiscovery(false);
        setCandidatesByPage({});
        setCurrentPage(1);
      },
      (error: unknown) => {
        toast.error(typeof error === "string" ? error : "Failed to add candidate");
      }
    ).add(() => setAddSubmitting(false));
  };

  const handleDiscover = (c: BaseModelCandidate) => {
    if (c.status !== "PENDING" && c.status !== "FAILED") return;
    setDiscoveringId(c.id);
    BaseModelCandidateService.discover(c.id, null, apiKey).subscribe(
      () => {
        toast.success(`Discovery complete for ${c.hf_model_id}`);
        setCandidatesByPage({});
      },
      (error: unknown) => {
        toast.error(
          typeof error === "string" ? error : "Discovery failed"
        );
      }
    ).add(() => setDiscoveringId(null));
  };

  const candidates = candidatesByPage[cacheKey] || [];
  const loading = isLoading && candidates.length === 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    Base Model Candidates
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1 mb-6">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Base Model Candidates
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Zero-day model support: add HuggingFace models for finetuning.
                Discovery runs by default and auto-promotes on success.
              </p>
            </div>

            {/* Search and Actions */}
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
              <div className="flex items-center gap-4 flex-1 max-w-md">
                <div className="relative flex-1">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by model ID or display name (e.g. qwen3.5)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchCandidates()}
                    className="pl-9"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchQuery("")}
                >
                  Clear
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchCandidates}
                  disabled={isLoading}
                >
                  <RefreshCwIcon
                    className={`w-4 h-4 mr-1.5 ${isLoading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
                <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                  <PlusIcon className="w-4 h-4 mr-1.5" />
                  Add Candidate
                </Button>
              </div>
            </div>

            {/* Summary Badge */}
            <div className="flex gap-2 mt-4">
              <Badge variant="secondary">{totalCandidates} total</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <BaseModelCandidatesTable
          candidates={candidates}
          loading={loading}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          totalItems={totalCandidates}
          discoveringId={discoveringId}
          onDiscover={handleDiscover}
        />
      </div>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Base Model Candidate</DialogTitle>
            <DialogDescription>
              Add a HuggingFace model for the zero-day pipeline. The model must
              exist on HuggingFace. Discovery runs by default.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="hf-model-id">HuggingFace Model ID *</Label>
              <Input
                id="hf-model-id"
                placeholder="Qwen/Qwen2.5-27B-Instruct"
                value={addHfModelId}
                onChange={(e) => setAddHfModelId(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="display-name">Display Name (optional)</Label>
              <Input
                id="display-name"
                placeholder="Derived from model ID if empty"
                value={addDisplayName}
                onChange={(e) => setAddDisplayName(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="skip-discovery"
                checked={addSkipDiscovery}
                onChange={(e) => setAddSkipDiscovery(e.target.checked)}
                className="rounded border-border"
              />
              <Label htmlFor="skip-discovery">Skip discovery (add only)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddDialogOpen(false)}
              disabled={addSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={addSubmitting}>
              {addSubmitting ? "Adding..." : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BaseModelCandidatesOverview;
